import { InjectableService } from '@/shared/services/InjectableService'
import { AxiosResponse } from 'axios'
import { submitApiRequest, getTenantPath, HttpMethod } from '@/services/aplosApi'
import { useUser } from '@/composables/useUser'
import axios from 'axios'
import type {
  TemplateMetadata,
  TemplateDetail,
  AvailableTemplate,
  UploadUrlResponse,
  TemplateListResponse,
} from '../models/index'


export interface ITemplateApiService {
  listTemplates(params?: { includeInactive?: boolean; pageSize?: number; nextToken?: string }): Promise<AxiosResponse<TemplateListResponse>>
  getTemplate(templateId: string): Promise<AxiosResponse<TemplateDetail>>
  getAvailableTemplates(): Promise<AxiosResponse<AvailableTemplate[]>>
  requestUploadUrl(name: string): Promise<AxiosResponse<UploadUrlResponse>>
  uploadFileToS3(url: string, fields: Record<string, string>, file: File, onProgress?: (percent: number) => void): Promise<void>
  confirmUpload(templateId: string, version: number): Promise<AxiosResponse<TemplateMetadata>>
  requestVersionUploadUrl(templateId: string): Promise<AxiosResponse<UploadUrlResponse>>
  updateStatus(templateId: string, status: 'active' | 'inactive'): Promise<AxiosResponse<TemplateMetadata>>
  getDownloadUrl(templateId: string, version?: number): Promise<AxiosResponse<{ downloadUrl: string }>>
}


export class TemplateApiService extends InjectableService implements ITemplateApiService {

  static readonly injectableKey = 'ITemplateApiService'

  get injectableKey() {
    return (this.constructor as typeof TemplateApiService).injectableKey
  }

  private getTenantId(): string {
    const { user } = useUser()
    if (!user.tenant_id) throw new Error('Tenant ID is required')
    return user.tenant_id
  }

  async listTemplates(params?: {
    includeInactive?: boolean
    pageSize?: number
    nextToken?: string
  }): Promise<AxiosResponse<TemplateListResponse>> {
    const tenantPath = getTenantPath(this.getTenantId())
    const queryParts: string[] = []
    if (params?.includeInactive) queryParts.push('include_inactive=true')
    if (params?.pageSize) queryParts.push(`page_size=${params.pageSize}`)
    if (params?.nextToken) queryParts.push(`next_token=${encodeURIComponent(params.nextToken)}`)
    const qs = queryParts.length ? `?${queryParts.join('&')}` : ''
    return await submitApiRequest(HttpMethod.GET, `${tenantPath}/templates${qs}`)
  }

  async getTemplate(templateId: string): Promise<AxiosResponse<TemplateDetail>> {
    const tenantPath = getTenantPath(this.getTenantId())
    return await submitApiRequest(HttpMethod.GET, `${tenantPath}/templates/${templateId}`)
  }

  async getAvailableTemplates(): Promise<AxiosResponse<AvailableTemplate[]>> {
    const tenantPath = getTenantPath(this.getTenantId())
    return await submitApiRequest(HttpMethod.GET, `${tenantPath}/templates/available`)
  }

  async requestUploadUrl(name: string): Promise<AxiosResponse<UploadUrlResponse>> {
    const tenantPath = getTenantPath(this.getTenantId())
    return await submitApiRequest(HttpMethod.POST, `${tenantPath}/templates/upload-url`, { name })
  }

  async uploadFileToS3(
    url: string,
    fields: Record<string, string>,
    file: File,
    onProgress?: (percent: number) => void
  ): Promise<void> {
    const formData = new FormData()

    // NOTE: field order matters for S3 pre-signed POST — fields must come before the file
    for (const key in fields) {
      formData.append(key, fields[key])
    }
    formData.append('file', file)

    await axios.post(url, formData, {
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          onProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total))
        }
      }
    })
  }

  async confirmUpload(templateId: string, _version: number): Promise<AxiosResponse<TemplateMetadata>> {
    const tenantPath = getTenantPath(this.getTenantId())
    return await submitApiRequest(HttpMethod.POST, `${tenantPath}/templates/${templateId}/confirm-upload`)
  }

  async requestVersionUploadUrl(templateId: string): Promise<AxiosResponse<UploadUrlResponse>> {
    const tenantPath = getTenantPath(this.getTenantId())
    return await submitApiRequest(HttpMethod.POST, `${tenantPath}/templates/${templateId}/versions`)
  }

  async updateStatus(templateId: string, status: 'active' | 'inactive'): Promise<AxiosResponse<TemplateMetadata>> {
    const tenantPath = getTenantPath(this.getTenantId())
    return await submitApiRequest(HttpMethod.PATCH, `${tenantPath}/templates/${templateId}/status`, { status })
  }

  async getDownloadUrl(templateId: string, version?: number): Promise<AxiosResponse<{ downloadUrl: string }>> {
    const tenantPath = getTenantPath(this.getTenantId())
    const qs = version != null ? `?version=${version}` : ''
    return await submitApiRequest(HttpMethod.GET, `${tenantPath}/templates/${templateId}/download-url${qs}`)
  }
}
